// src/components/IndustryInsightsSummaryModal.js
// Industry Insights Summary modal with date range selection, Word export, and email sharing

import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    IconButton,
    Chip,
    Divider,
    Alert,
    TextField,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import {
    Close as CloseIcon,
    Download as DownloadIcon,
    Share as ShareIcon,
    ExpandMore as ExpandMoreIcon,
    TrendingUp as TrendingUpIcon,
    Warning as WarningIcon,
    Lightbulb as LightbulbIcon,
    Campaign as SignalIcon,
    Recommend as RecommendIcon,
    People as PeopleIcon,
    Sync as SynergyIcon
} from '@mui/icons-material';
import { Document, Packer, Paragraph, HeadingLevel, TextRun, Table as DocxTable, TableRow as DocxTableRow, TableCell as DocxTableCell, WidthType, BorderStyle } from 'docx';
import { toast } from 'react-toastify';
import { functions, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import ShareModal from './ShareModal';

const IndustryInsightsSummaryModal = ({ 
    open, 
    onClose, 
    industries = [],  // Array of industry strings to populate dropdown
    scope = 'fcfo',   // 'fcfo' for User Portal, 'firm' for Firm Portal
    user,
    userData 
}) => {
    // Date range state (default: last 30 days)
    const getDefaultDates = () => {
        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 30);
        
        return {
            from: thirtyDaysAgo.toISOString().split('T')[0],
            to: today.toISOString().split('T')[0]
        };
    };

    const [selectedIndustry, setSelectedIndustry] = useState('');
    const [dateFrom, setDateFrom] = useState(getDefaultDates().from);
    const [dateTo, setDateTo] = useState(getDefaultDates().to);
    const [isLoading, setIsLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        themes: true,
        challenges: true,
        opportunities: true,
        signals: false,
        actions: true,
        highlights: false,
        synergies: false
    });

    // Track previous open state to detect fresh open transitions
    const wasOpenRef = useRef(false);

    // Reset state only on a fresh open (closed → open transition).
    // Previously, having `industries` in the dependency array caused resets
    // when the industries array reference changed (e.g., from a user token refresh
    // triggering a re-fetch), which wiped reportData mid-generation.
    useEffect(() => {
        if (open && !wasOpenRef.current) {
            const defaults = getDefaultDates();
            setDateFrom(defaults.from);
            setDateTo(defaults.to);
            setSelectedIndustry(industries.length === 1 ? industries[0] : '');
            setReportData(null);
            setExpandedSections({
                themes: true,
                challenges: true,
                opportunities: true,
                signals: false,
                actions: true,
                highlights: false,
                synergies: false
            });
        }
        wasOpenRef.current = open;
    }, [open, industries]);

    // Validate date range (max 90 days)
    const validateDateRange = () => {
        const from = new Date(dateFrom);
        const to = new Date(dateTo);
        const daysDiff = Math.ceil((to - from) / (1000 * 60 * 60 * 24));
        
        if (daysDiff > 90) {
            toast.error('Date range cannot exceed 90 days.');
            return false;
        }
        if (from > to) {
            toast.error('Start date must be before end date.');
            return false;
        }
        return true;
    };

    // Generate report
    const handleGenerate = async () => {
        if (!selectedIndustry) {
            toast.error('Please select an industry.');
            return;
        }
        if (!dateFrom || !dateTo) {
            toast.error('Please select both start and end dates.');
            return;
        }
        if (!validateDateRange()) {
            return;
        }

        setIsLoading(true);
        setReportData(null);

        try {
            const generateReport = httpsCallable(
                functions,
                'generateIndustryInsightsSummary',
                { timeout: 900000 }
            );

            const result = await generateReport({
                industry: selectedIndustry,
                dateFrom,
                dateTo,
                scope
            });

            if (result.data.success) {
                setReportData(result.data.industryInsightsSummary);
                toast.success(result.data.message);

                // --- Phase 5: Persist Industry Insights to clientReports ---
                try {
                  const summary = result.data.industryInsightsSummary;
                  const execSummary = summary.executiveSummary || '';
                  const plainPreview = execSummary
                    .replace(/#{1,6}\s/g, '')
                    .replace(/\*\*([^*]+)\*\*/g, '$1')
                    .replace(/\*([^*]+)\*/g, '$1')
                    .replace(/\n+/g, ' ')
                    .trim()
                    .substring(0, 200);

                  await addDoc(collection(db, 'clientReports'), {
                    clientId: null,  // Cross-client report — no single clientId
                    fcfoId: user?.uid,
                    reportType: 'industry_insights',
                    title: `Industry Insights: ${selectedIndustry}`,
                    content: execSummary,
                    contentPreview: plainPreview,
                    structuredData: summary,  // Full structured object for expanded rendering
                    industry: selectedIndustry,
                    dateFrom,
                    dateTo,
                    generatedAt: serverTimestamp(),
                    generatedBy: user?.uid,
                    organizationId: userData?.organizationId || null,
                    provider: summary.metadata?.provider || 'Claude Sonnet 4.5',
                    topicTags: []
                  });
                } catch (persistError) {
                  console.error('[Phase5] Failed to persist Industry Insights:', persistError);
                }
                // --- End Phase 5 ---
            } else {
                // Handle specific error cases
                if (result.data.error === 'insufficient_clients') {
                    toast.warning(result.data.message);
                } else if (result.data.error === 'insufficient_content') {
                    toast.warning(result.data.message);
                } else {
                    throw new Error(result.data.message || 'Failed to generate report');
                }
            }
        } catch (error) {
            console.error('Industry Insights generation failed:', error);
            toast.error(error.message || 'Failed to generate Industry Insights Summary');
        } finally {
            setIsLoading(false);
        }
    };

    // Toggle section expansion
    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Format date range for display
    const formatDateRange = () => {
        const from = new Date(dateFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const to = new Date(dateTo).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return `${from} – ${to}`;
    };

    // Get priority color
    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'HIGH': return 'error';
            case 'MEDIUM': return 'warning';
            case 'LOW': return 'success';
            default: return 'default';
        }
    };

    // Export to Word document
    const handleExportToWord = async () => {
        if (!reportData) {
            toast.error('No report data to export.');
            return;
        }

        try {
            const { metadata, topThemes, executiveSummary, commonChallenges, sharedOpportunities, industrySignals, recommendedActions, clientHighlights, crossClientSynergies } = reportData;

            // Build document sections
            const children = [
                // Title
                new Paragraph({
                    text: `Industry Insights Summary: ${metadata.industry}`,
                    heading: HeadingLevel.TITLE
                }),
                // Metadata header
                new Paragraph({
                    children: [
                        new TextRun({ text: 'Period: ', bold: true }),
                        new TextRun({ text: formatDateRange() }),
                        new TextRun({ text: '  |  ' }),
                        new TextRun({ text: 'Scope: ', bold: true }),
                        new TextRun({ text: scope === 'firm' ? 'Firm-Wide' : 'My Clients' }),
                        new TextRun({ text: '  |  ' }),
                        new TextRun({ text: 'Generated: ', bold: true }),
                        new TextRun({ text: new Date(metadata.generatedAt).toLocaleDateString() })
                    ],
                    spacing: { after: 100 }
                }),
                // Counts
                new Paragraph({
                    children: [
                        new TextRun({ text: 'Clients Analyzed: ', bold: true }),
                        new TextRun({ text: `${metadata.clientCount}` }),
                        new TextRun({ text: '  |  ' }),
                        new TextRun({ text: 'Content: ', bold: true }),
                        new TextRun({ text: `${metadata.counts.questions} Q&A • ${metadata.counts.signals} Signals • ${metadata.counts.decisionMemos} Memos` })
                    ],
                    spacing: { after: 300 }
                }),
                new Paragraph({ text: '────────────────────────────────────────', spacing: { after: 200 } }),

                // EXECUTIVE SUMMARY
                new Paragraph({
                    text: 'EXECUTIVE SUMMARY',
                    heading: HeadingLevel.HEADING_1,
                    spacing: { before: 300, after: 200 }
                }),
                new Paragraph({
                    text: executiveSummary || 'No executive summary generated.',
                    spacing: { after: 300 }
                }),

                new Paragraph({ text: '────────────────────────────────────────', spacing: { before: 200, after: 200 } }),

                // TOP THEMES
                new Paragraph({
                    text: 'TOP THEMES',
                    heading: HeadingLevel.HEADING_1,
                    spacing: { before: 300, after: 200 }
                })
            ];

            // Add themes
            if (topThemes && topThemes.length > 0) {
                topThemes.forEach(theme => {
                    children.push(
                        new Paragraph({
                            children: [
                                new TextRun({ text: '• ', bold: true }),
                                new TextRun({ text: theme.theme, bold: true }),
                                new TextRun({ text: ` (${theme.frequency} occurrences across ${theme.clientCount} client${theme.clientCount > 1 ? 's' : ''})` })
                            ],
                            spacing: { after: 50 }
                        })
                    );
                });
            } else {
                children.push(new Paragraph({ text: 'No themes identified.', spacing: { after: 200 } }));
            }

            children.push(new Paragraph({ text: '────────────────────────────────────────', spacing: { before: 200, after: 200 } }));

            // COMMON CHALLENGES
            children.push(
                new Paragraph({
                    text: 'COMMON CHALLENGES',
                    heading: HeadingLevel.HEADING_1,
                    spacing: { before: 300, after: 200 }
                })
            );

            if (commonChallenges && commonChallenges.length > 0) {
                commonChallenges.forEach((challenge, idx) => {
                    children.push(
                        new Paragraph({
                            children: [
                                new TextRun({ text: `${idx + 1}. `, bold: true }),
                                new TextRun({ text: challenge.challenge, bold: true })
                            ],
                            spacing: { after: 50 }
                        }),
                        new Paragraph({
                            children: [
                                new TextRun({ text: '   Affected: ', bold: true }),
                                new TextRun({ text: challenge.affectedClients?.join(', ') || 'Multiple clients' })
                            ],
                            spacing: { after: 50 }
                        })
                    );
                    if (challenge.evidence) {
                        children.push(
                            new Paragraph({
                                children: [
                                    new TextRun({ text: '   Evidence: ', bold: true }),
                                    new TextRun({ text: challenge.evidence })
                                ],
                                spacing: { after: 100 }
                            })
                        );
                    }
                });
            } else {
                children.push(new Paragraph({ text: 'No common challenges identified.', spacing: { after: 200 } }));
            }

            children.push(new Paragraph({ text: '────────────────────────────────────────', spacing: { before: 200, after: 200 } }));

            // SHARED OPPORTUNITIES
            children.push(
                new Paragraph({
                    text: 'SHARED OPPORTUNITIES',
                    heading: HeadingLevel.HEADING_1,
                    spacing: { before: 300, after: 200 }
                })
            );

            if (sharedOpportunities && sharedOpportunities.length > 0) {
                sharedOpportunities.forEach((opp, idx) => {
                    children.push(
                        new Paragraph({
                            children: [
                                new TextRun({ text: `${idx + 1}. `, bold: true }),
                                new TextRun({ text: opp.opportunity, bold: true })
                            ],
                            spacing: { after: 50 }
                        }),
                        new Paragraph({
                            children: [
                                new TextRun({ text: '   Relevant to: ', bold: true }),
                                new TextRun({ text: opp.relevantClients?.join(', ') || 'Multiple clients' })
                            ],
                            spacing: { after: 50 }
                        })
                    );
                    if (opp.potentialImpact) {
                        children.push(
                            new Paragraph({
                                children: [
                                    new TextRun({ text: '   Potential Impact: ', bold: true }),
                                    new TextRun({ text: opp.potentialImpact })
                                ],
                                spacing: { after: 100 }
                            })
                        );
                    }
                });
            } else {
                children.push(new Paragraph({ text: 'No shared opportunities identified.', spacing: { after: 200 } }));
            }

            children.push(new Paragraph({ text: '────────────────────────────────────────', spacing: { before: 200, after: 200 } }));

            // RECOMMENDED ACTIONS
            children.push(
                new Paragraph({
                    text: 'RECOMMENDED ACTIONS',
                    heading: HeadingLevel.HEADING_1,
                    spacing: { before: 300, after: 200 }
                })
            );

            if (recommendedActions && recommendedActions.length > 0) {
                recommendedActions.forEach((action, idx) => {
                    children.push(
                        new Paragraph({
                            children: [
                                new TextRun({ text: `${idx + 1}. [${action.priority}] `, bold: true }),
                                new TextRun({ text: action.action, bold: true })
                            ],
                            spacing: { after: 50 }
                        }),
                        new Paragraph({
                            children: [
                                new TextRun({ text: '   Rationale: ', bold: true }),
                                new TextRun({ text: action.rationale })
                            ],
                            spacing: { after: 50 }
                        })
                    );
                    if (action.affectedClients?.length > 0) {
                        children.push(
                            new Paragraph({
                                children: [
                                    new TextRun({ text: '   Affects: ', bold: true }),
                                    new TextRun({ text: action.affectedClients.join(', ') })
                                ],
                                spacing: { after: 100 }
                            })
                        );
                    }
                });
            } else {
                children.push(new Paragraph({ text: 'No recommended actions identified.', spacing: { after: 200 } }));
            }

            // CLIENT HIGHLIGHTS (if present)
            if (clientHighlights && clientHighlights.length > 0) {
                children.push(new Paragraph({ text: '────────────────────────────────────────', spacing: { before: 200, after: 200 } }));
                children.push(
                    new Paragraph({
                        text: 'CLIENT HIGHLIGHTS',
                        heading: HeadingLevel.HEADING_1,
                        spacing: { before: 300, after: 200 }
                    })
                );

                clientHighlights.forEach(highlight => {
                    children.push(
                        new Paragraph({
                            children: [
                                new TextRun({ text: '• ', bold: true }),
                                new TextRun({ text: highlight.clientName, bold: true })
                            ],
                            spacing: { after: 50 }
                        })
                    );
                    if (highlight.keyActivity) {
                        children.push(
                            new Paragraph({
                                children: [
                                    new TextRun({ text: '   Key Activity: ' }),
                                    new TextRun({ text: highlight.keyActivity })
                                ],
                                spacing: { after: 30 }
                            })
                        );
                    }
                    if (highlight.uniqueInsight) {
                        children.push(
                            new Paragraph({
                                children: [
                                    new TextRun({ text: '   Insight: ' }),
                                    new TextRun({ text: highlight.uniqueInsight })
                                ],
                                spacing: { after: 100 }
                            })
                        );
                    }
                });
            }

            // Footer
            children.push(
                new Paragraph({ text: '────────────────────────────────────────', spacing: { before: 300, after: 100 } }),
                new Paragraph({
                    children: [
                        new TextRun({ text: 'Generated by SavvaiEdge™', italics: true }),
                        new TextRun({ text: ` on ${new Date().toLocaleString()}`, italics: true })
                    ],
                    spacing: { after: 100 }
                })
            );

            // Create document
            const doc = new Document({
                sections: [{ children }]
            });

            // Generate and download
            const blob = await Packer.toBlob(doc);
            const filename = `Industry_Insights_${metadata.industry.replace(/[^a-zA-Z0-9]/g, '_')}_${dateFrom}_to_${dateTo}.docx`;
            
            const link = document.createElement('a');
            const url = window.URL.createObjectURL(blob);
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Industry Insights Summary exported to Word!');
        } catch (error) {
            console.error('Word export failed:', error);
            toast.error('Failed to export Word document.');
        }
    };

    // Handle email share
    const handleShare = async (sharePayload) => {
        if (!reportData) return { success: false, error: 'No report to share' };

        try {
            const shareReport = httpsCallable(functions, 'shareIndustryInsightsReportByEmail');
            await shareReport({
                recipientEmail: sharePayload.recipientEmail,
                personalMessage: sharePayload.personalMessage,
                reportData: reportData
            });
            toast.success('Industry Insights Summary shared successfully!');
            return { success: true };
        } catch (error) {
            console.error('Share failed:', error);
            toast.error(`Failed to share report: ${error.message}`);
            return { success: false, error: error.message };
        }
    };

    // Render the report content
    const renderReportContent = () => {
        if (!reportData) return null;

        const { metadata, topThemes, executiveSummary, commonChallenges, sharedOpportunities, industrySignals, recommendedActions, clientHighlights, crossClientSynergies, clients } = reportData;

        return (
            <Box>
                {/* Metadata Header */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                        {metadata.industry}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <strong>Period:</strong> {formatDateRange()} &nbsp;|&nbsp;
                        <strong>Scope:</strong> {scope === 'firm' ? 'Firm-Wide' : 'My Clients'} &nbsp;|&nbsp;
                        <strong>Clients:</strong> {metadata.clientCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        <strong>Content Analyzed:</strong> {metadata.counts.questions} Q&A • {metadata.counts.signals} Signals • {metadata.counts.decisionMemos} Memos
                    </Typography>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Executive Summary */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Executive Summary
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                        {executiveSummary || 'No executive summary generated.'}
                    </Typography>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Top Themes Section */}
                <Accordion 
                    expanded={expandedSections.themes} 
                    onChange={() => toggleSection('themes')}
                    sx={{ mb: 1 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUpIcon color="primary" />
                            <Typography fontWeight={600}>
                                Top Themes ({topThemes?.length || 0})
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        {topThemes && topThemes.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {topThemes.map((theme, idx) => (
                                    <Chip
                                        key={idx}
                                        label={`${theme.theme} (${theme.frequency}x, ${theme.clientCount} client${theme.clientCount > 1 ? 's' : ''})`}
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No themes identified in this period.
                            </Typography>
                        )}
                    </AccordionDetails>
                </Accordion>

                {/* Common Challenges Section */}
                <Accordion 
                    expanded={expandedSections.challenges} 
                    onChange={() => toggleSection('challenges')}
                    sx={{ mb: 1 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WarningIcon color="error" />
                            <Typography fontWeight={600}>
                                Common Challenges ({commonChallenges?.length || 0})
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        {commonChallenges && commonChallenges.length > 0 ? (
                            <Stack spacing={2}>
                                {commonChallenges.map((challenge, idx) => (
                                    <Box key={idx} sx={{ pl: 1, borderLeft: '3px solid #f44336' }}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {challenge.challenge}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Affected: {challenge.affectedClients?.join(', ') || 'Multiple clients'}
                                        </Typography>
                                        {challenge.evidence && (
                                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                <em>Evidence: {challenge.evidence}</em>
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No common challenges identified.
                            </Typography>
                        )}
                    </AccordionDetails>
                </Accordion>

                {/* Shared Opportunities Section */}
                <Accordion 
                    expanded={expandedSections.opportunities} 
                    onChange={() => toggleSection('opportunities')}
                    sx={{ mb: 1 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LightbulbIcon color="success" />
                            <Typography fontWeight={600}>
                                Shared Opportunities ({sharedOpportunities?.length || 0})
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        {sharedOpportunities && sharedOpportunities.length > 0 ? (
                            <Stack spacing={2}>
                                {sharedOpportunities.map((opp, idx) => (
                                    <Box key={idx} sx={{ pl: 1, borderLeft: '3px solid #4caf50' }}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {opp.opportunity}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Relevant to: {opp.relevantClients?.join(', ') || 'Multiple clients'}
                                        </Typography>
                                        {opp.potentialImpact && (
                                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                <em>Potential Impact: {opp.potentialImpact}</em>
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No shared opportunities identified.
                            </Typography>
                        )}
                    </AccordionDetails>
                </Accordion>

                {/* Industry Signals Section */}
                {industrySignals && industrySignals.length > 0 && (
                    <Accordion 
                        expanded={expandedSections.signals} 
                        onChange={() => toggleSection('signals')}
                        sx={{ mb: 1 }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SignalIcon color="secondary" />
                                <Typography fontWeight={600}>
                                    Industry Signals ({industrySignals.length})
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack spacing={2}>
                                {industrySignals.map((signal, idx) => (
                                    <Box key={idx} sx={{ pl: 1, borderLeft: '3px solid #9c27b0' }}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {signal.signal}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Impacts: {signal.impactedClients?.join(', ') || 'Multiple clients'}
                                        </Typography>
                                        {signal.implication && (
                                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                <em>Implication: {signal.implication}</em>
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* Recommended Actions Section */}
                <Accordion 
                    expanded={expandedSections.actions} 
                    onChange={() => toggleSection('actions')}
                    sx={{ mb: 1 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <RecommendIcon color="warning" />
                            <Typography fontWeight={600}>
                                Recommended Actions ({recommendedActions?.length || 0})
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        {recommendedActions && recommendedActions.length > 0 ? (
                            <Stack spacing={2}>
                                {recommendedActions.map((action, idx) => (
                                    <Box key={idx} sx={{ pl: 1, borderLeft: '3px solid #ff9800' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                            <Chip 
                                                label={action.priority} 
                                                size="small" 
                                                color={getPriorityColor(action.priority)}
                                            />
                                            <Typography variant="body2" fontWeight={600}>
                                                {action.action}
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption" color="text.secondary">
                                            {action.rationale}
                                        </Typography>
                                        {action.affectedClients?.length > 0 && (
                                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                Affects: {action.affectedClients.join(', ')}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No recommended actions identified.
                            </Typography>
                        )}
                    </AccordionDetails>
                </Accordion>

                {/* Client Highlights Section */}
                {clientHighlights && clientHighlights.length > 0 && (
                    <Accordion 
                        expanded={expandedSections.highlights} 
                        onChange={() => toggleSection('highlights')}
                        sx={{ mb: 1 }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PeopleIcon color="info" />
                                <Typography fontWeight={600}>
                                    Client Highlights ({clientHighlights.length})
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack spacing={2}>
                                {clientHighlights.map((highlight, idx) => (
                                    <Box key={idx} sx={{ pl: 1, borderLeft: '3px solid #2196f3' }}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {highlight.clientName}
                                        </Typography>
                                        {highlight.keyActivity && (
                                            <Typography variant="caption" color="text.secondary">
                                                Key Activity: {highlight.keyActivity}
                                            </Typography>
                                        )}
                                        {highlight.uniqueInsight && (
                                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                <em>{highlight.uniqueInsight}</em>
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* Cross-Client Synergies Section */}
                {crossClientSynergies && crossClientSynergies.length > 0 && (
                    <Accordion 
                        expanded={expandedSections.synergies} 
                        onChange={() => toggleSection('synergies')}
                        sx={{ mb: 1 }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SynergyIcon color="primary" />
                                <Typography fontWeight={600}>
                                    Cross-Client Synergies ({crossClientSynergies.length})
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack spacing={2}>
                                {crossClientSynergies.map((synergy, idx) => (
                                    <Box key={idx} sx={{ pl: 1, borderLeft: '3px solid #1976d2' }}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {synergy.synergy}
                                        </Typography>
                                        {synergy.suggestion && (
                                            <Typography variant="caption" color="text.secondary">
                                                {synergy.suggestion}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* Generation info */}
                <Divider sx={{ mt: 2, mb: 1 }} />
                <Typography variant="caption" color="text.secondary">
                    Generated: {new Date(metadata.generatedAt).toLocaleString()} by {metadata.generatedByName || user?.displayName || user?.email || 'Unknown'}
                </Typography>
            </Box>
        );
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { minHeight: '70vh' }
                }}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight={600}>
                            {isLoading 
                                ? 'Generating Industry Insights...' 
                                : reportData 
                                    ? `Industry Insights: ${reportData.metadata.industry}`
                                    : 'Industry Insights Summary'
                            }
                        </Typography>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    {/* Selection Controls (shown before generation) */}
                    {!reportData && !isLoading && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Generate a cross-client analysis for clients in the same industry. 
                                Identifies patterns, common challenges, shared opportunities, and recommended actions.
                            </Typography>
                            
                            <Stack spacing={2}>
                                <FormControl fullWidth size="small" disabled={isLoading}>
                                    <InputLabel>Select Industry</InputLabel>
                                    <Select
                                        value={selectedIndustry}
                                        onChange={(e) => setSelectedIndustry(e.target.value)}
                                        label="Select Industry"
                                    >
                                        {industries.map((industry, idx) => (
                                            <MenuItem key={idx} value={industry}>
                                                {industry}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Stack direction="row" spacing={2} alignItems="center">
                                    <TextField
                                        label="From"
                                        type="date"
                                        value={dateFrom}
                                        onChange={(e) => setDateFrom(e.target.value)}
                                        size="small"
                                        disabled={isLoading}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ width: 180 }}
                                    />
                                    <TextField
                                        label="To"
                                        type="date"
                                        value={dateTo}
                                        onChange={(e) => setDateTo(e.target.value)}
                                        size="small"
                                        disabled={isLoading}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ width: 180 }}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleGenerate}
                                        disabled={!selectedIndustry || !dateFrom || !dateTo}
                                    >
                                        Generate Report
                                    </Button>
                                </Stack>

                                <Alert severity="info" sx={{ mt: 1 }}>
                                    Requires at least 2 clients in the selected industry with 5+ total content items (Q&A, Signals, or Memos) in the date range.
                                </Alert>
                            </Stack>
                        </Box>
                    )}

                    {/* Loading State */}
                    {isLoading && (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            minHeight="300px"
                        >
                            <CircularProgress size={48} sx={{ mb: 2 }} />
                            <Typography variant="body1" color="text.secondary">
                                Analyzing cross-client patterns and themes...
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                This may take 30-60 seconds depending on data volume.
                            </Typography>
                        </Box>
                    )}

                    {/* Report Content */}
                    {reportData && !isLoading && renderReportContent()}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    {reportData && !isLoading && (
                        <>
                            <Button
                                onClick={() => {
                                    setReportData(null);
                                    const defaults = getDefaultDates();
                                    setDateFrom(defaults.from);
                                    setDateTo(defaults.to);
                                }}
                                color="inherit"
                            >
                                New Report
                            </Button>
                            <Box sx={{ flexGrow: 1 }} />
                        </>
                    )}
                    <Button onClick={onClose} color="inherit">
                        Close
                    </Button>
                    {reportData && !isLoading && (
                        <>
                            <Button
                                onClick={() => setIsShareModalOpen(true)}
                                startIcon={<ShareIcon />}
                            >
                                Share
                            </Button>
                            <Button
                                onClick={handleExportToWord}
                                variant="contained"
                                startIcon={<DownloadIcon />}
                            >
                                Export to Word
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Share Modal */}
            {isShareModalOpen && reportData && (
                <ShareModal
                    open={isShareModalOpen}
                    onClose={() => setIsShareModalOpen(false)}
                    item={reportData}
                    itemType="industry_insights"
                    onShare={handleShare}
                />
            )}
        </>
    );
};

export default IndustryInsightsSummaryModal;